import React from 'react'
import PropTypes from 'prop-types'
import { DialogContent, DialogTitle, Divider, IconButton, Dialog as MuiDialog, Typography, Box } from '@mui/material'
import closeIcon from 'images/closeIcon.svg'
import { usePriceComparisonChanges } from 'hooks/usePriceComparisonChanges'
import PriceChangesInfo from './PriceChangesInfo'
import { numberFormat } from 'utils/number-format-util'
import { useTranslation } from 'react-i18next'
import PriceComparisonDialogBlock from './PriceComparisonDialogBlock'

const PriceComparisonDialog = ({
    priceData,
    dealerName,
    dealerPhoneNumber,
    transactionType,
    isDialogOpen = false,
    closeDialog = () => {}
}) => {
    const { t } = useTranslation()
    const { increasesResponse = {}, decreaseResponse = {} } = priceData

    let increase_text
    let decreases_text
    if (transactionType === 'lease') {
        increase_text = t('increases_lease')
        decreases_text = t('decreases_lease')
    } else if (transactionType === 'Cash') {
        increase_text = t('increases_case')
        decreases_text = t('decreases_case')
    } else {
        increase_text = t('increases_finance')
        decreases_text = t('decreases_finance')
    }

    const {
        priceIncrease,
        priceDecrease,
        totalChanges,
        formattedIncreaseTotal,
        formattedDecreaseTotal,
        formattedTotalPrice,
        increasesPopOverData,
        decreasesPopOverData,
        totalPopOverData,
        transformOrigin
    } = usePriceComparisonChanges(priceData)

    const displayPrice = numberFormat(formattedTotalPrice?.replace('-', '').replace('+', ''))

    return (
        <MuiDialog
            fullScreen
            open={isDialogOpen}
            onClose={closeDialog}
            disableEscapeKeyDown={true}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
            data-testid="disclosures-dialog"
            sx={{
                color: 'common.fordBlue.main',
                zIndex: 3000,
                textAlign: 'left',
                padding: '0px',
                '.MuiModal-backdrop': {
                    backgroundColor: 'action.disabledBackground'
                },
                '.MuiDialog-paper': {
                    color: 'common.fordBlue.main',
                    backgroundColor: 'background.default',
                    borderRadius: { xs: 0, smd: 4 },
                    maxWidth: { xs: '100%', smd: 540, md: 640 },
                    padding: '0px 0px 15px',
                    height: 'auto',
                    maxHeight: { xs: '100%', smd: '98%' }
                }
            }}
        >
            <DialogTitle
                id="alert-dialog-title"
                sx={{
                    p: 4
                }}
            >
                <Typography
                    tabIndex={0}
                    sx={{
                        typography: 'mediumTitle',
                        color: 'common.charcoal.main'
                    }}
                >
                    {t('price_changes')}
                </Typography>
                <IconButton
                    id="close"
                    aria-label="close"
                    onClick={closeDialog}
                    sx={{
                        color: 'common.charcoal.main',
                        p: 2,
                        position: 'absolute',
                        right: '5px',
                        top: '5px'
                    }}
                >
                    <img src={closeIcon} alt={'close modal'} width={24} height={24} />
                </IconButton>
            </DialogTitle>

            <DialogContent
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'auto',
                    padding: '0px 24px'
                }}
            >
                <Typography
                    tabIndex={0}
                    sx={{
                        padding: '0 16px 24px',
                        typography: { xs: 'regularBody2', md: 'regularBody1' },
                        color: 'common.charcoal.main'
                    }}
                    data-testid="total-changes"
                >
                    {totalChanges > 1
                        ? t('price_changes_description', {
                              total: totalChanges,
                              dealerName: dealerName,
                              dealerPhoneNumber: dealerPhoneNumber
                          })
                        : t('price_change_description', {
                              total: totalChanges,
                              dealerName: dealerName,
                              dealerPhoneNumber: dealerPhoneNumber
                          })}
                </Typography>

                <Typography
                    sx={{
                        p: 6,
                        color: 'common.charcoal.main',
                        backgroundImage: 'linear-gradient(128deg, #F5F6F7 35.5%, #FEFEFE 140.67%)',
                        borderRadius: 4
                    }}
                >
                    <Typography
                        sx={{
                            alignItems: 'center',
                            display: 'flex',
                            overflow: 'initial',
                            justifyContent: 'center',
                            gap: 1
                        }}
                    >
                        <Typography
                            tabIndex={0}
                            component="span"
                            sx={{
                                typography: { xs: 'regularBody1', md: 'regularSubTitle' },
                                lineHeight: '28px'
                            }}
                        >
                            {t('total_net_change')}
                        </Typography>
                        <PriceChangesInfo
                            popOverData={totalPopOverData}
                            transformOrigin={transformOrigin}
                            isTotalPopOver={true}
                        />
                    </Typography>

                    <Box
                        tabIndex={0}
                        sx={{
                            display: 'flex',
                            gap: '3px',
                            alignItems: 'center',
                            component: 'span',
                            justifyContent: 'center',
                            pt: 4
                        }}
                        data-testid="total-amt"
                    >
                        <Typography
                            variant="smallHeadline2"
                            component="span"
                            sx={{
                                color: 'common.charcoal.main',
                                typography: { xs: 'mediumSubTitle', md: 'mediumTitle' }
                            }}
                        >
                            {formattedTotalPrice
                                ? (Math.sign(formattedTotalPrice) === -1 && '-$' + displayPrice) || '+$' + displayPrice
                                : '$' + displayPrice}
                        </Typography>
                    </Box>
                </Typography>

                {Object.keys(increasesResponse)?.length > 0 && (
                    <PriceComparisonDialogBlock
                        testId={'increases'}
                        changesTitle={increase_text}
                        priceResponse={increasesResponse}
                        transformOrigin={transformOrigin}
                        popOverData={increasesPopOverData}
                        priceIncrease={priceIncrease}
                        formattedTotal={formattedIncreaseTotal}
                    />
                )}

                {Object.keys(increasesResponse)?.length > 0 && Object.keys(decreaseResponse)?.length > 0 && (
                    <Divider
                        sx={{ height: '1px', backgroundColor: '#D1D5D9', mx: 6 }}
                        orientation="vertical"
                        aria-hidden="true"
                    ></Divider>
                )}

                {Object.keys(decreaseResponse)?.length > 0 && (
                    <PriceComparisonDialogBlock
                        testId={'decreases'}
                        changesTitle={decreases_text}
                        priceResponse={decreaseResponse}
                        transformOrigin={transformOrigin}
                        popOverData={decreasesPopOverData}
                        priceIncrease={priceDecrease}
                        formattedTotal={formattedDecreaseTotal}
                    />
                )}
            </DialogContent>
        </MuiDialog>
    )
}

PriceComparisonDialog.propTypes = {
    isDialogOpen: PropTypes.bool,
    closeDialog: PropTypes.func,
    priceData: PropTypes.object,
    dealerName: PropTypes.string,
    dealerPhoneNumber: PropTypes.string,
    transactionType: PropTypes.string
}

export default PriceComparisonDialog
